import { ApolloLink, Observable, FetchResult, Operation } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

import { toast } from 'react-toastify';

import { logWarning } from 'helpers/logger';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './storage';

type PendingRequest = { retry: () => void; abort: (err: Error) => void };

const NETWORK_FAILURE_MESSAGE = 'Connexion au serveur perdue, la session est conservée. Réessayez.';

// Délai avant de rejouer une subscription dont le refresh a échoué sans verdict du serveur.
const SUBSCRIPTION_RETRY_DELAY_MS = 10000;

// Comme totmobile. Un refresh abandonné a pu être traité par le serveur : il retrouve le token au prochain
// essai (fenêtre de grâce, puis récupération d'une rotation perdue), on ne perd donc pas la session.
const REFRESH_TIMEOUT_MS = 10000;

let isRefreshing = false;
let pendingRequests: PendingRequest[] = [];

function resolvePendingRequests(): void {
    pendingRequests.forEach(({ retry }) => retry());
    pendingRequests = [];
}

// Reject queued requests so their observer errors out instead of hanging forever.
// Without this, consumers (e.g. useQuery hooks) would stay in loading state when
// the refresh fails between an in-flight retry and the redirect.
function rejectPendingRequests(reason: Error): void {
    pendingRequests.forEach(({ abort }) => abort(reason));
    pendingRequests = [];
}

/**
 * Pourquoi le refresh n'a pas abouti — la distinction porte tout ce fichier.
 *
 * `session_expired` = le serveur a tranché (401), ou il n'y a plus rien à présenter : la session
 * est morte, on déconnecte. `network_error` = on n'a pas eu de réponse exploitable (API qui
 * redémarre, 5xx, coupure réseau) : on ne sait RIEN de l'état de la session, donc on garde les
 * tokens. Les confondre, c'est jeter un refresh token encore valide au premier hoquet — ce qui
 * était le comportement précédent, et l'essentiel des déconnexions en dev où l'API redémarre à
 * chaque sauvegarde. Même découpage que totops et totmobile.
 */
type RefreshResult =
    | { type: 'success'; accessToken: string; refreshToken: string }
    | { type: 'session_expired'; reason: 'no_token' | 'server_401' | 'partial_response' }
    | { type: 'network_error'; status?: number };

// `still_unauthenticated` : refresh réussi, mais l'opération rejouée avec le token neuf est encore refusée.
type LogoutReason = Extract<RefreshResult, { type: 'session_expired' }>['reason'] | 'still_unauthenticated';

// Serialize refresh across tabs/windows of this origin via Web Locks API.
// Without this, two tabs waking from sleep both POST /auth/refresh with the same
// refresh token — one rotates successfully, the other gets 401 and logs the user out.
async function fetchNewTokens(apiUrl: string): Promise<RefreshResult> {
    const initialRefreshToken = getRefreshToken();
    if (!initialRefreshToken) return { type: 'session_expired', reason: 'no_token' };

    // Le stockage s'écrit ici, sous le verrou : l'onglet suivant relit le token neuf (ou un stockage vidé)
    // au lieu de représenter celui que le serveur vient de consommer.
    const runRefresh = async (): Promise<RefreshResult> => {
        // Re-read after acquiring the lock: another tab may have rotated while we waited.
        const currentRefreshToken = getRefreshToken();
        if (!currentRefreshToken) return { type: 'session_expired', reason: 'no_token' };
        if (currentRefreshToken !== initialRefreshToken) {
            const accessToken = getAccessToken();
            if (!accessToken) return { type: 'session_expired', reason: 'no_token' };
            return { type: 'success', accessToken, refreshToken: currentRefreshToken };
        }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
        let response: Response;
        try {
            response = await fetch(`${apiUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken: currentRefreshToken }),
                signal: controller.signal,
            });
        } finally {
            clearTimeout(timeout);
        }

        if (!response.ok) {
            // Safety net: if storage rotated between request send and response,
            // another tab succeeded — use its tokens instead of forcing logout.
            const storedRefresh = getRefreshToken();
            const storedAccess = getAccessToken();
            if (storedRefresh && storedRefresh !== currentRefreshToken && storedAccess) {
                return { type: 'success', accessToken: storedAccess, refreshToken: storedRefresh };
            }

            // Le serveur a bien vu le token et l'a refusé : rotation déjà consommée, token
            // révoqué ou expiré. C'est le SEUL cas où la session est réellement morte.
            if (response.status === 401) {
                clearTokens();
                return { type: 'session_expired', reason: 'server_401' };
            }

            return { type: 'network_error', status: response.status };
        }

        const data = await response.json();
        // Exiger les DEUX tokens : sans ça, `setTokens` écrivait `'undefined'` comme access token
        // et gardait le refresh token que le serveur vient de consommer. Le serveur a répondu 200,
        // donc la rotation a eu lieu : c'est une session morte, pas un hoquet (même règle que
        // freefood-app). Un corps illisible, lui, fait rejeter `response.json()` et reste
        // transitoire : c'est la signature d'un intermédiaire (proxy, portail captif), pas de l'API.
        if (!data?.accessToken || !data?.refreshToken) {
            clearTokens();
            return { type: 'session_expired', reason: 'partial_response' };
        }
        setTokens(data.accessToken, data.refreshToken);
        return { type: 'success', accessToken: data.accessToken, refreshToken: data.refreshToken };
    };

    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return navigator.locks.request('totem-token-refresh', runRefresh);
    }
    return runRefresh();
}

let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * Un seul `/auth/refresh` à la fois dans l'onglet, qu'il vienne d'une requête refusée ou de la socket
 * (`socket-session.ts`). Entre onglets, c'est le Web Lock de `fetchNewTokens` qui sérialise.
 */
export function refreshSession(apiUrl: string): Promise<RefreshResult> {
    refreshInFlight ??= fetchNewTokens(apiUrl).finally(() => {
        refreshInFlight = null;
    });
    return refreshInFlight;
}

function isSubscriptionOperation(operation: Operation): boolean {
    const definition = getMainDefinition(operation.query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
}

function hasErrorCode(result: FetchResult, code: string): boolean {
    return Boolean(result.errors?.some((error) => (error.extensions?.code as string) === code));
}

function isUnauthenticated(result: FetchResult): boolean {
    return Boolean(
        result.errors?.some(
            (error) => (error.extensions?.code as string) === 'AHERA' || error.message === 'Authentification requise',
        ),
    );
}

// AHEEX : access token expiré. Il ne vient que de HTTP : un token expiré n'ouvre pas la socket, que l'API ferme à son
// échéance (`socket-session.ts`). AHERA : le serveur ne reconnaît pas l'access token, mais le refresh token est un
// credential distinct, qu'on essaie avant de déconnecter. Une panne côté serveur ne produit plus d'AHERA
// (`AUTH_UNAVAILABLE`). Le refresh rouvre ensuite la socket avec le token neuf (`onTokenRefreshed`).
function needsTokenRefresh(result: FetchResult): boolean {
    return hasErrorCode(result, 'AHEEX') || isUnauthenticated(result);
}

/**
 * La requête est partie avec un access token déjà remplacé : un refresh a abouti pendant qu'elle était en vol.
 * La rejouer avec le token stocké suffit, une nouvelle rotation serait inutile. Pas pour une subscription : la
 * socket ne porte pas de token par opération.
 */
function replacedTokenOf(operation: Operation): string | null {
    if (isSubscriptionOperation(operation)) return null;

    const sentToken: string | undefined = operation.getContext().headers?.authorization;
    const storedToken = getAccessToken();
    return storedToken && sentToken && sentToken !== storedToken ? storedToken : null;
}

/**
 * Les AHEEX individuels ne sont pas remontés (attendus, et rattrapés ici), donc la déconnexion
 * forcée est le seul événement d'auth qui vaille une alerte : c'est celui que l'utilisateur subit.
 */
function redirectToLogin(reason: LogoutReason, operationName?: string): void {
    logWarning({
        message: 'Token refresh link: session expired, triggering logout',
        tags: { reason, operationName: operationName ?? 'anonymous' },
    });
    clearTokens();
    window.location.href = '/login';
}

/** Session morte constatée par la socket (refresh refusé avant une reconnexion) : même sortie que pour une requête. */
export function expireSession(reason: LogoutReason): void {
    rejectPendingRequests(new Error('Session expired'));
    redirectToLogin(reason, 'socket');
}

/**
 * Échec sans verdict du serveur : on garde les tokens, on prévient, et on rend l'erreur réseau à
 * remonter — à l'appelant de décider quoi en faire (rejet pour une query/mutation, nouvelle
 * tentative pour une subscription).
 *
 * Les requêtes en attente sont rejetées plutôt que rejouées : elles repartiraient avec le même
 * access token expiré, redéclencheraient un refresh, et boucleraient tant que l'API est absente.
 * Un rejet laisse les hooks sortir de `loading`, et la prochaine action (ou un refetch Apollo)
 * retentera avec le refresh token intact.
 */
function reportTransientFailure({
    operationName,
    status,
    cause,
}: {
    operationName?: string;
    status?: number;
    cause?: unknown;
}): Error {
    logWarning({
        message: 'Token refresh link: transient failure, keeping tokens',
        tags: {
            status: status ? String(status) : 'no_response',
            operationName: operationName ?? 'anonymous',
        },
        contexts: { cause: cause instanceof Error ? cause.message : cause },
    });

    // `toastId` fixe : une API absente produit un cycle de refresh par vague de requêtes, et
    // l'utilisateur n'a pas besoin d'une pile de toasts identiques pour une seule panne.
    toast.error(NETWORK_FAILURE_MESSAGE, { autoClose: 10000, toastId: 'token-refresh-network' });

    const error = new Error(NETWORK_FAILURE_MESSAGE);
    rejectPendingRequests(error);
    return error;
}

export function createTokenRefreshLink(apiUrl: string, onTokenRefreshed?: () => void): ApolloLink {
    return new ApolloLink((operation, forward) => {
        return new Observable<FetchResult>((observer) => {
            const isSubscription = isSubscriptionOperation(operation);

            const applyToken = (token: string): void => {
                operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                    headers: { ...headers, authorization: token },
                }));
            };

            /**
             * Une subscription est déjà terminée côté serveur quand son refus arrive (`next` suivi d'un
             * `complete`), et `useSubscription` ne se réabonne pas après une erreur : la rejeter couperait le
             * temps réel de l'écran jusqu'au rechargement. On la rejoue donc plus tard. `observer.closed` :
             * l'écran s'est désabonné entre-temps.
             */
            const resubscribeLater = (): void => {
                setTimeout(() => {
                    if (observer.closed) return;
                    handle();
                }, SUBSCRIPTION_RETRY_DELAY_MS);
            };

            // Sortie d'un refresh raté sans verdict du serveur : une query/mutation est rejetée.
            const failOrRetry = (error: Error): void => {
                if (isSubscription) {
                    resubscribeLater();
                    return;
                }
                observer.error(error);
            };

            // Rejeu avec le token neuf. Une query ou une mutation encore en AHERA : le serveur ne reconnaît
            // vraiment pas cette session.
            const replayAfterRefresh = (): void => {
                forward(operation).subscribe({
                    next: (result) => {
                        if (!isSubscription && isUnauthenticated(result)) {
                            redirectToLogin('still_unauthenticated', operation.operationName);
                        }
                        observer.next(result);
                    },
                    error: (error) => observer.error(error),
                    complete: () => observer.complete(),
                });
            };

            // Chaque envoi a son propre drapeau : le `complete` d'un envoi dont on a repris la main ne doit pas
            // terminer l'opération, celui de l'envoi qui le remplace le fera.
            function handle(): void {
                let isHandledHere = false;

                forward(operation).subscribe({
                    next: (result: FetchResult): void => {
                        // Panne côté serveur pendant la vérification du token : ni refresh ni déconnexion.
                        if (isSubscription && hasErrorCode(result, 'AUTH_UNAVAILABLE')) {
                            isHandledHere = true;
                            resubscribeLater();
                            return;
                        }

                        if (!needsTokenRefresh(result)) {
                            observer.next(result);
                            return;
                        }

                        isHandledHere = true;

                        const storedToken = replacedTokenOf(operation);
                        if (storedToken) {
                            applyToken(storedToken);
                            handle();
                            return;
                        }

                        if (isRefreshing) {
                            pendingRequests.push({
                                retry: () => {
                                    applyToken(getAccessToken() || '');
                                    replayAfterRefresh();
                                },
                                abort: failOrRetry,
                            });
                            return;
                        }

                        isRefreshing = true;

                        refreshSession(apiUrl)
                            .then((refreshResult) => {
                                if (refreshResult.type === 'session_expired') {
                                    rejectPendingRequests(new Error('Session expired'));
                                    redirectToLogin(refreshResult.reason, operation.operationName);
                                    return;
                                }

                                if (refreshResult.type === 'network_error') {
                                    failOrRetry(
                                        reportTransientFailure({
                                            operationName: operation.operationName,
                                            status: refreshResult.status,
                                        }),
                                    );
                                    return;
                                }

                                // La socket se rouvre avec le token neuf.
                                onTokenRefreshed?.();
                                applyToken(refreshResult.accessToken);
                                resolvePendingRequests();
                                replayAfterRefresh();
                            })
                            .catch((err) => {
                                // `fetch` qui rejette (API injoignable, DNS, offline, timeout) ou corps
                                // illisible (intermédiaire qui répond à la place de l'API) : aucune réponse
                                // de l'API, donc aucune raison de considérer la session morte.
                                failOrRetry(
                                    reportTransientFailure({ operationName: operation.operationName, cause: err }),
                                );
                            })
                            .finally(() => {
                                isRefreshing = false;
                            });
                    },
                    error: (error: unknown): void => observer.error(error),
                    complete: (): void => {
                        if (!isHandledHere) {
                            observer.complete();
                        }
                    },
                });
            }

            handle();
        });
    });
}
